import { useState } from "react";
import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { SelectControl } from "../../components/form/formControls";
import toast from "react-hot-toast";
import useDebounce from "../../hooks/useDebounce";
import SearchableSelect from "../../components/selectForm";
import {
  fetchAllFuel,
  fuelLabel,
  useFuelEmployees,
} from "../../hooks/useFuelAllowance";
import { fuelAllowanceCall } from "../../api/fuelAllowance";
import { exportExcelFromApi } from "../../utils/tools";
import FuelTabsHeader from "./FuelTabsHeader";
import { FuelExportButton, FuelSearchInput } from "./FuelToolbar";
import CheckinRegistrationTable from "../deviceManager/checkinRegistrationTable";
import FuelActionModal from "./FuelActionModal";

// Xăng xe - tab Nhân viên: loại + mức xăng xe hiện tại, HCNS đổi trực tiếp

// Popup đổi xăng xe: "Chuyển sang" = không đăng ký hoặc 1 trong 3 mức (bỏ lựa chọn đang có).
// Số tiền các mức lấy từ BE (Cài đặt chung)
const NOT_REGISTERED = "NONE";
const buildTargetOptions = (fuelLevelOptions) => [
  { value: NOT_REGISTERED, label: "Không đăng ký nhận xăng xe" },
  ...(fuelLevelOptions || []).map((o) => ({
    value: String(o.level),
    label: fuelLabel(true, o.level, fuelLevelOptions),
  })),
];
const currentTarget = (row) =>
  row?.fuelRegistered ? String(row.fuelLevel || 1) : NOT_REGISTERED;

const FuelOptions = [
  { key: "true", value: "Đăng ký nhận xăng xe" },
  { key: "false", value: "Không đăng ký nhận xăng xe" },
];

// Không dùng key 0 vì ô chọn trả về 0 khi bấm xoá lọc
// Lọc "Khối bộ phận" (Khối kinh doanh / hỗ trợ kinh doanh / giám đốc)
const DepartmentOptions = [
  { key: "1", value: "Khối kinh doanh" },
  { key: "SUPPORT", value: "Khối hỗ trợ kinh doanh" },
  { key: "2", value: "Khối giám đốc" },
];

// "Đơn vị" tách thành 3 cột Khối / Vùng / Phòng ban (26/10/2026)
const headers = [
  { label: "Mã NV", key: "code", width: "8%" },
  { label: "Tên NV", key: "name", width: "13%" },
  { label: "Khối bộ phận", key: "department", width: "9%" },
  { label: "Loại nhân sự", key: "businessCategory", width: "9%" },
  { label: "Khối", key: "saleBlock", width: "9%" },
  { label: "Vùng", key: "saleArea", width: "9%" },
  { label: "Phòng ban", key: "supportDepartment", width: "9%" },
  { label: "Xăng xe", key: "fuel", width: "13%" },
  { label: "Cập nhật lúc", key: "updatedAt", width: "8%" },
  { label: "Thao tác", key: "action", width: "13%" },
];

const exportMapping = {
  code: "Mã NV",
  name: "Tên NV",
  startDate: "Ngày vào làm",
  department: "Khối bộ phận",
  businessCategory: "Loại nhân sự",
  saleBlock: "Khối",
  saleArea: "Vùng",
  supportDepartment: "Phòng ban",
  fuel: "Xăng xe",
  fuelRegistered: "Nhận xăng xe",
  fuelLevelLabel: "Mức xăng xe",
  fuelAmount: "Số tiền xăng xe",
  pending: "Đơn chờ duyệt",
  hasPendingRequest: "Đang có đơn chờ duyệt",
  alwaysCheckout: "Luôn phải chấm ra",
  updatedAt: "Cập nhật lúc",
};

const TableFuelEmployees = ({ tab, setTab }) => {
  const [search, setSearch] = useState("");
  const [fuelRegistered, setFuelRegistered] = useState("");
  const [department, setDepartment] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  const filters = { search: debouncedSearch, fuelRegistered, department };

  const {
    items,
    raw,
    refetch,
    isLoading,
    isRefetching,
    totalPages,
    setCurrentPage,
    currentPage,
    entries,
    startEntry,
    endEntry,
  } = useFuelEmployees(filters);

  // NV đang mở popup đổi loại xăng xe + lựa chọn "Chuyển sang" (bắt buộc)
  const [editing, setEditing] = useState(null);
  const [target, setTarget] = useState(null);
  const [targetError, setTargetError] = useState(null);

  const openEdit = (row) => {
    setEditing(row);
    setTarget(null);
    setTargetError(null);
  };

  const submitToggle = async (note) => {
    const row = editing;
    if (!target) {
      setTargetError({ message: "Vui lòng chọn loại xăng xe chuyển sang" });
      return;
    }
    const next = target !== NOT_REGISTERED;
    try {
      await fuelAllowanceCall.updateEmployee(row.id, {
        fuelRegistered: next,
        ...(next ? { fuelLevel: Number(target) } : {}),
        ...(note ? { note } : {}),
      });
      await refetch();
      toast.success("Cập nhật thành công");
      setEditing(null);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleExport = () =>
    exportExcelFromApi({
      fetcher: () => fetchAllFuel("employees", filters, exportMapping),
      mapping: exportMapping,
      filename: "danh_sach_xang_xe.xlsx",
    });

  const renderCell = (key, row) => {
    if (key === "fuel") {
      return (
        <Box>
          <Text
            color={
              row.fuelRegistered
                ? "var(--color-status-employ)"
                : "var(--color-option-employee-hover)"
            }
            fontWeight={600}
          >
            {row.fuel}
          </Text>
          {row.hasPendingRequest ? (
            <Text fontSize="12px" color="var(--text-red-employee)">
              Có đơn chờ duyệt
            </Text>
          ) : null}
        </Box>
      );
    }
    if (key === "action") {
      return (
        <Button
          bg={"var(--color-main)"}
          color={"var(--color-option-employee-hover)"}
          border={"1px solid var(--color-option-employee-hover)"}
          fontSize={"12px"}
          fontWeight={"600"}
          _hover={{ filter: "brightness(90%)" }}
          onClick={() => openEdit(row)}
          h={"28px"}
          minH={"28px"}
        >
          Đổi xăng xe
        </Button>
      );
    }
    return <Text>{row[key]}</Text>;
  };

  const applyDate = raw?.applyDate;

  return (
    <>
      <FuelTabsHeader
        tab={tab}
        setTab={setTab}
        onRefresh={() => refetch()}
        rightSlot={<FuelExportButton onClick={handleExport} />}
      />

      <Box mt={{ base: "23px" }}>
        <Flex
          backgroundColor="var(--color-main)"
          borderRadius={{ base: "12px" }}
          flexDirection={"column"}
        >
          <Flex
            p={"16px 16px 12px 16px"}
            gap={{ base: "16px 32px" }}
            alignItems={"center"}
            flexWrap={"wrap"}
          >
            <FuelSearchInput
              onChange={(v) => {
                setSearch(v);
                setCurrentPage(1);
              }}
            />
            <SearchableSelect
              isShowSearch={false}
              options={FuelOptions}
              initialSelectedOption={{ key: 0, value: "Chọn loại xăng xe" }}
              setValue={(v) => {
                setFuelRegistered(v || "");
                setCurrentPage(1);
              }}
            />
            <SearchableSelect
              isShowSearch={false}
              options={DepartmentOptions}
              initialSelectedOption={{ key: 0, value: "Chọn khối bộ phận" }}
              setValue={(v) => {
                setDepartment(v === "SUPPORT" ? "0" : v ? String(v) : "");
                setCurrentPage(1);
              }}
            />
          </Flex>

          <Text px="16px" pb="8px" fontSize="12px" color="var(--color-option-employee-hover)">
            {!raw?.enabled
              ? "Xăng xe đang tắt (Cài đặt chung): mọi NV vẫn phải chấm ra."
              : `${
                  applyDate
                    ? `Áp dụng từ ${applyDate.split("-").reverse().join("/")}`
                    : "Đang áp dụng"
                }: NV đăng ký nhận xăng xe phải chấm ra mới đủ công, NV không đăng ký thì không cần chấm ra. Riêng Khối hỗ trợ kinh doanh luôn phải chấm ra.`}{" "}
            NV gửi đơn thay đổi (chọn mức) trên app từ ngày 1 đến ngày {raw?.deadlineDay || 10} hàng tháng, quản lý duyệt trên app rồi HCNS duyệt.
          </Text>

          <CheckinRegistrationTable
            headers={headers}
            rows={items}
            renderCell={renderCell}
            isLoaded={!isLoading && !isRefetching}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={(p) => setCurrentPage(p.selected + 1)}
            startEntry={startEntry}
            endEntry={endEntry}
            entries={entries}
          />
        </Flex>
      </Box>

      <FuelActionModal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title="Đổi loại xăng xe"
        fields={
          editing
            ? [
                { label: "Nhân viên", value: `${editing.code} - ${editing.name}` },
                { label: "Hiện tại", value: editing.fuel },
              ]
            : []
        }
        message={
          editing && target
            ? `${
                target !== NOT_REGISTERED
                  ? "Đăng ký nhận xăng xe: phải chấm ra mới đủ công."
                  : editing.alwaysCheckout
                    ? "Không đăng ký nhận xăng xe: nhân viên Khối hỗ trợ kinh doanh vẫn phải chấm ra mới đủ công."
                    : "Không đăng ký nhận xăng xe: không cần chấm ra."
              } Áp dụng từ lần chấm vào tiếp theo của nhân viên.`
            : ""
        }
        submitText="Xác nhận"
        onSubmit={submitToggle}
      >
        <Flex mb={"12px"}>
          <SelectControl
            label="Chuyển sang"
            dataOption={buildTargetOptions(raw?.fuelLevelOptions).filter(
              (o) => o.value !== currentTarget(editing),
            )}
            selectedMain={target}
            placeholder="Chọn loại xăng xe"
            onChange={(v) => {
              setTarget(v);
              setTargetError(null);
            }}
            errorMessage={targetError}
            mwidth={"100%"}
            isRequired={true}
          />
        </Flex>
      </FuelActionModal>
    </>
  );
};

export default TableFuelEmployees;
